import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../style/Auth/Register.css";
import GoogleButton from "../GoogleButton";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import logoImage from "../../assets/blasterprime.png";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff } from "lucide-react";
import ModalError from "../ModalError";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    password_confirmation: "",
    whatsapp_number: "",
    usdt_network_reg: "TRC20",
    usdt_address: "",
    referral_code: "",
  });

  const [errorModal, setErrorModal] = useState({
    show: false,
    message: "",
  });

  const [searchParams] = useSearchParams();

  // State untuk debounce validation
  const [validationErrors, setValidationErrors] = useState({});
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingWhatsapp, setIsCheckingWhatsapp] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [whatsappAvailable, setWhatsappAvailable] = useState(null);

  // Effect untuk membaca parameter referral dari URL
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      // Kirim request ke backend untuk mencatat visit
      const trackVisit = async () => {
        try {
          const response = await fetch(`https://blasterprime.com/api/referrals/track/${refCode}`, {
            method: "GET",
            credentials: "include",
          });

          if (response.ok) {
            console.log("Visit tracked successfully");
          } else {
            console.error("Failed to track visit:", response.status);
          }
        } catch (err) {
          console.error("Error tracking visit:", err);
        }
      };

      trackVisit();

      setFormData((prev) => ({
        ...prev,
        referral_code: refCode,
      }));

      toast.success(`Referral code detected: ${refCode}`, {
        position: "top-center",
        autoClose: 3000,
      });
    }
  }, [searchParams]);

  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [hCaptchaToken, setHCaptchaToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const hCaptchaRef = useRef(null);
  const navigate = useNavigate();

  // Debounce validation untuk email
  useEffect(() => {
    const validateEmail = async () => {
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setEmailAvailable(null);
        setValidationErrors(prev => ({ ...prev, email: null }));
        return;
      }

      setIsCheckingEmail(true);
      try {
        const response = await axios.get(`https://blasterprime.com/api/auth/check-email?email=${encodeURIComponent(formData.email)}`);
        
        if (response.data.exists) {
          setEmailAvailable(false);
          setValidationErrors(prev => ({ 
            ...prev, 
            email: "This email is already registered. Please use a different email or login." 
          }));
        } else {
          setEmailAvailable(true);
          setValidationErrors(prev => ({ ...prev, email: null }));
        }
      } catch (err) {
        console.error("Error checking email:", err);
        setEmailAvailable(null);
      } finally {
        setIsCheckingEmail(false);
      }
    };

    const timeoutId = setTimeout(() => {
      if (formData.email) {
        validateEmail();
      }
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [formData.email]);

  // Debounce validation untuk WhatsApp
  useEffect(() => {
    const validateWhatsapp = async () => {
      if (!formData.whatsapp_number || formData.whatsapp_number.length < 10) {
        setWhatsappAvailable(null);
        setValidationErrors(prev => ({ ...prev, whatsapp_number: null }));
        return;
      }

      setIsCheckingWhatsapp(true);
      try {
        const response = await axios.get(`https://blasterprime.com/api/auth/check-whatsapp?number=${encodeURIComponent(formData.whatsapp_number)}`);
        
        if (response.data.exists) {
          setWhatsappAvailable(false);
          setValidationErrors(prev => ({ 
            ...prev, 
            whatsapp_number: "This WhatsApp number is already registered. Please use a different number." 
          }));
        } else {
          setWhatsappAvailable(true);
          setValidationErrors(prev => ({ ...prev, whatsapp_number: null }));
        }
      } catch (err) {
        console.error("Error checking whatsapp:", err);
        setWhatsappAvailable(null);
      } finally {
        setIsCheckingWhatsapp(false);
      }
    };

    const timeoutId = setTimeout(() => {
      if (formData.whatsapp_number) {
        validateWhatsapp();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.whatsapp_number]);

  const showErrorToast = (message) => {
    setErrorModal({ show: true, message });
  };

  const showSuccessToast = (message) => {
    toast.success(message, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear form errors saat mengetik
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
    
    // Reset validation untuk email/whatsapp saat berubah
    if (name === 'email') {
      setEmailAvailable(null);
    }
    if (name === 'whatsapp_number') {
      setWhatsappAvailable(null);
    }
  };

  const handleHCaptchaVerify = (token) => {
    setHCaptchaToken(token);
  };

  const handleHCaptchaError = () => {
    setHCaptchaToken(null);
    showErrorToast("Captcha verification failed. Please try again.");
  };

  const handleHCaptchaExpire = () => {
    setHCaptchaToken(null);
    showErrorToast("Captcha session expired. Please verify again.");
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = "Full name is required";
      isValid = false;
    }

    if (formData.referral_code && !/^[A-Z0-9]{8,12}$/.test(formData.referral_code)) {
      errors.referral_code = "Referral code must be 8-12 alphanumeric characters";
      isValid = false;
    }

    if (!formData.username.trim()) {
      errors.username = "Username is required";
      isValid = false;
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      errors.username = "Only letters, numbers and underscores allowed";
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email";
      isValid = false;
    } else if (emailAvailable === false) {
      errors.email = "This email is already registered. Please use a different email or login.";
      isValid = false;
    }

    if (!formData.password) {
      errors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 8) {
      errors.password = "Minimum 8 characters required";
      isValid = false;
    }

    if (!formData.password_confirmation) {
      errors.password_confirmation = "Please confirm your password";
      isValid = false;
    } else if (formData.password !== formData.password_confirmation) {
      errors.password_confirmation = "Passwords do not match";
      isValid = false;
    }

    if (formData.whatsapp_number) {
      if (!/^\+?[\d\s-]+$/.test(formData.whatsapp_number)) {
        errors.whatsapp_number = "Please enter a valid WhatsApp number";
        isValid = false;
      } else if (whatsappAvailable === false) {
        errors.whatsapp_number = "This WhatsApp number is already registered. Please use a different number.";
        isValid = false;
      }
    }

    if (formData.usdt_address && formData.usdt_address.length < 10) {
      errors.usdt_address = "USDT address must be at least 10 characters";
      isValid = false;
    }

    if (!acceptTerms) {
      showErrorToast("You must accept the terms and conditions");
      isValid = false;
    }

    if (!hCaptchaToken) {
      showErrorToast("Please complete the captcha verification");
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validasi tambahan sebelum submit
    if (emailAvailable === false) {
      showErrorToast("This email is already registered. Please use a different email or login.");
      return;
    }

    if (whatsappAvailable === false) {
      showErrorToast("This WhatsApp number is already registered. Please use a different number.");
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await axios.post("https://blasterprime.com/api/auth/register", {
        ...formData,
        acceptTerms,
        hCaptchaToken,
      });

      if (response.data.success) {
        showSuccessToast("Registration successful!");
        navigate("/account-checking", {
          state: {
            email: formData.email,
            isActive: response.data.is_active,
          },
        });
      }
    } catch (err) {
      console.error("Registration error (frontend):", err.response?.data || err);

      let errorMessage = "Registration failed. Please try again.";

      if (err.response?.data) {
        const data = err.response.data;

        if (data.error) {
          errorMessage = data.error;
        } else if (data.details) {
          const details = Object.values(data.details).join("\n");
          errorMessage = details;
          setFormErrors(data.details);
        } else if (data.code === "INVALID_CAPTCHA") {
          errorMessage = "Captcha verification failed. Please try again.";
          hCaptchaRef.current.resetCaptcha();
          setHCaptchaToken(null);
        } else if (data.code === "EMAIL_EXISTS") {
          errorMessage = "Email already exists. Please use a different email.";
        } else if (data.code === "WHATSAPP_EXISTS") {
          errorMessage = "WhatsApp number already exists. Please use a different number.";
        }
      }

      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled =
    isLoading ||
    !acceptTerms ||
    !hCaptchaToken ||
    !formData.name ||
    !formData.username ||
    !formData.email ||
    !formData.password ||
    formData.password.length < 8 ||
    !formData.password_confirmation ||
    formData.password !== formData.password_confirmation ||
    emailAvailable === false || // Email sudah terdaftar
    whatsappAvailable === false || // WhatsApp sudah terdaftar
    Object.keys(formErrors).length > 0;

  return (
    <>
      <div className="register-wrapper">
        <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

        <div className="register-left">
          <div className="branding">
            <img src={logoImage} alt="BLASTERC" className="logo-img-reg" />
            <p className="tagline">Let's Grow Your Business with Us</p>
          </div>
        </div>

        <div className="register-right">
          <div className="register-form">
            <h2>Create Your Account Today</h2>
            <p className="subtitle-reg">Join us and experience the convenience of managing WhatsApp and SMS broadcasts in a single, integrated platform.</p>

            <div className="google-login">
              <GoogleButton />
              <div className="separator">
                <div className="border1"></div>
                <span>or</span>
                <div className="border1"></div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group-register">
                <input type="text" name="name" placeholder="Full Name" value={formData.name} onChange={handleChange} className={formErrors.name ? "input-error" : ""} />
                {formErrors.name && <span className="error-text">{formErrors.name}</span>}
              </div>

              <div className="form-group-register">
                <input type="text" name="username" placeholder="Username" value={formData.username} onChange={handleChange} className={formErrors.username ? "input-error" : ""} />
                {formErrors.username && <span className="error-text">{formErrors.username}</span>}
              </div>

              <div className="form-group-register">
                <div className="input-with-validation">
                  <input 
                    type="text" 
                    name="whatsapp_number" 
                    placeholder="WhatsApp Number (Optional)" 
                    value={formData.whatsapp_number} 
                    onChange={handleChange} 
                    className={`${formErrors.whatsapp_number ? "input-error" : ""} ${isCheckingWhatsapp ? "checking" : ""} ${whatsappAvailable === true ? "input-valid" : ""}`} 
                  />
                  {isCheckingWhatsapp && <span className="checking-spinner">Checking...</span>}
                </div>
                {formErrors.whatsapp_number && <span className="error-text">{formErrors.whatsapp_number}</span>}
                {whatsappAvailable === true && formData.whatsapp_number && !formErrors.whatsapp_number && (
                  <span className="success-text">✓ WhatsApp number is available</span>
                )}
              </div>

              <div className="form-group-register-register">
                <div className="usdt-input-group-reg">
                  <select name="usdt_network_reg" value={formData.usdt_network_reg} onChange={handleChange} className={`usdt-network-select ${formErrors.usdt_address ? "input-error" : ""}`}>
                    <option value="TRC20">TRC20</option>
                    <option value="BEP20">BEP20</option>
                    <option value="ERC20">ERC20</option>
                  </select>
                  <input
                    type="text"
                    name="usdt_address"
                    placeholder={`USDT ${formData.usdt_network_reg} Wallet Address (Optional)`}
                    value={formData.usdt_address}
                    onChange={handleChange}
                    className={`usdt-address-input ${formErrors.usdt_address ? "input-error" : ""}`}
                  />
                </div>
                {formErrors.usdt_address && <span className="error-text">{formErrors.usdt_address}</span>}
              </div>

              <div className="form-group-register">
                <div className="input-with-validation">
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="Email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    className={`${formErrors.email ? "input-error" : ""} ${isCheckingEmail ? "checking" : ""} ${emailAvailable === true ? "input-valid" : ""}`} 
                  />
                  {isCheckingEmail && <span className="checking-spinner">Checking...</span>}
                </div>
                {formErrors.email && <span className="error-text">{formErrors.email}</span>}
                {emailAvailable === true && !formErrors.email && (
                  <span className="success-text">✓ Email is available</span>
                )}
              </div>

              {/* Password */}
              <div className="form-group-register">
                <div className="password-wrapper">
                  <input type={showPassword ? "text" : "password"} name="password" placeholder="Password (min 8 characters)" value={formData.password} onChange={handleChange} className={formErrors.password ? "input-error" : ""} />
                  <span className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </span>
                </div>
                {formErrors.password && <span className="error-text">{formErrors.password}</span>}
              </div>

              {/* Confirm Password */}
              <div className="form-group-register">
                <div className="password-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="password_confirmation"
                    placeholder="Confirm Password"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    className={formErrors.password_confirmation ? "input-error" : ""}
                  />
                  <span className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </span>
                </div>
                {formErrors.password_confirmation && <span className="error-text">{formErrors.password_confirmation}</span>}
              </div>

              <div className="form-group-register">
                <input type="text" name="referral_code" placeholder="Referral Code (Optional)" value={formData.referral_code} onChange={handleChange} className={formErrors.referral_code ? "input-error" : ""} />
                {formErrors.referral_code && <span className="error-text">{formErrors.referral_code}</span>}

                {searchParams.get("ref") && <small className="referral-info">Referral code detected from invitation link</small>}
              </div>

              <div className="checkbox-group">
                <input type="checkbox" id="acceptTerms" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
                <label htmlFor="acceptTerms">
                  I have read and agree to the{" "}
                  <a href="/terms" target="_blank">
                    Terms and Conditions
                  </a>{" "}
                  of Service.
                </label>
              </div>

              <div className="hcaptcha-container">
                <HCaptcha sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY} onVerify={handleHCaptchaVerify} onExpire={handleHCaptchaExpire} onError={handleHCaptchaError} ref={hCaptchaRef} />
              </div>

              <button type="submit" disabled={isSubmitDisabled} className={`submit-btn-register ${isSubmitDisabled ? "button-disabled" : ""}`}>
                {isLoading ? (
                  <>
                    <span className="spinner"></span> Registering...
                  </>
                ) : (
                  "Register Now"
                )}
              </button>
            </form>

            <p className="login-link">
              Already have an account? <a href="/login">Log in now.</a>
            </p>
          </div>
        </div>
      </div>
      <ModalError show={errorModal.show} message={errorModal.message} onClose={() => setErrorModal({ show: false, message: "" })} />
    </>
  );
};

export default Register;